/**
 * useTableSeed — host-side state for the Query Editor's "Query a table"
 * flow: picker open/anchor state, the command-palette entry point, and the
 * pick handler that (1) writes `SELECT <first N rows> * FROM <table>;` into
 * the buffer at the caret through the editor's undo-safe `executeEdits`,
 * (2) runs that statement through the normal cursor-statement execute path,
 * and (3) opens the compact Visual Query Editor on it so WHERE / ORDER BY /
 * columns are a click away. The buffer stays the single source of truth —
 * the seed is plain SQL the user can keep typing into.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useAnalytics } from '../../../../core/analytics/useAnalytics';
import { databaseEngineIdToSqlEngine } from '../../../../core/engines/databaseEngineIdToSqlEngine';
import type { DatabaseEngineId } from '../../../../core/engines';
import { useBetaFeature } from '../../../../core/beta';
import type { DatabaseObject } from '../../../database-objects/models/databaseObjectTypes';
import type { ObjectsLoadResult } from '../../../database-objects/services/ensureObjectsLoaded';
import type { SqlMonacoHandle } from '../../common/utils/sqlMonacoTypes';
import type { CompactVqeSource } from '../../visual-query-editor/compact/CompactVqePopover';
import { buildTableSeedSql } from './buildTableSeedSql';
import { recentSeedScopeKey, useRecentSeedTablesStore } from './recentSeedTablesStore';
import { tableSeedGroups, type TableSeedTarget } from './tableSeedGroups';
import type { TableSeedPickOptions } from './TableSeedPicker';

/** Window event the command palette dispatches to the ACTIVE query tab. */
export const QUERY_TABLE_EVENT = 'jam:query-editor:query-table';

export type TableSeedSource = 'gutter' | 'inline' | 'hint' | 'palette';

type UseTableSeedParams = {
  readonly isActive: boolean;
  readonly engineId: DatabaseEngineId;
  readonly connectionId: string | undefined;
  readonly database: string | undefined;
  readonly editorRef: React.RefObject<SqlMonacoHandle | null>;
  readonly databaseObjects: readonly DatabaseObject[] | undefined;
  /** The last object load of this connection + database failed (see useEnsureObjectsLoaded). */
  readonly databaseObjectsFailure: ObjectsLoadResult | undefined;
  readonly retryDatabaseObjects: () => void;
  readonly isExecuting: boolean;
  readonly isGeneratingScript: boolean;
  /** Runs one statement's SQL (the cursor-statement execute path). */
  readonly executeStatement: (sql: string) => void;
  readonly openCompactEdit: (source: CompactVqeSource) => void;
};

export type TableSeedState = ReturnType<typeof useTableSeed>;

/**
 * What the picker can list. It shows tables and views only, so a failed
 * procedure / function listing must not keep it waiting: when the table and
 * view listings succeeded, their objects are enough. When one of THEM failed,
 * `tableListError` carries the engine's message.
 */
export function resolveTableSeedObjects(
  databaseObjects: readonly DatabaseObject[] | undefined,
  failure: ObjectsLoadResult | undefined,
): { objects: readonly DatabaseObject[] | undefined; tableListError: string | undefined } {
  if (databaseObjects) return { objects: databaseObjects, tableListError: undefined };
  if (!failure) return { objects: undefined, tableListError: undefined };
  const tableListFailure = failure.failures.find((f) => f.type === 'table' || f.type === 'view');
  if (tableListFailure) return { objects: undefined, tableListError: tableListFailure.error };
  return { objects: failure.partialObjects, tableListError: undefined };
}

export function useTableSeed({
  isActive,
  engineId,
  connectionId,
  database,
  editorRef,
  databaseObjects,
  databaseObjectsFailure,
  retryDatabaseObjects,
  isExecuting,
  isGeneratingScript,
  executeStatement,
  openCompactEdit,
}: UseTableSeedParams) {
  const { track } = useAnalytics();
  const vqeEnabled = useBetaFeature('visual-query-editor');
  const enabled =
    vqeEnabled &&
    !!connectionId &&
    databaseEngineIdToSqlEngine(engineId) !== null &&
    !isExecuting &&
    !isGeneratingScript;

  const [picker, setPicker] = useState<{ open: boolean; anchorTop: number; source: TableSeedSource }>({
    open: false,
    anchorTop: 0,
    source: 'gutter',
  });

  const recentKeys = useRecentSeedTablesStore((s) =>
    connectionId ? s.recentByScope[recentSeedScopeKey(connectionId, database)] : undefined,
  );
  const { objects: pickerObjects, tableListError } = resolveTableSeedObjects(databaseObjects, databaseObjectsFailure);
  const groups = useMemo(() => tableSeedGroups(pickerObjects, recentKeys), [pickerObjects, recentKeys]);

  const retryLoad = useCallback(() => {
    // engine only — never the database name or the error text.
    track('feature:query_table_seed_retry', { engine: engineId });
    retryDatabaseObjects();
  }, [track, engineId, retryDatabaseObjects]);

  const open = useCallback((source: TableSeedSource, anchorTop?: number) => {
    let top = anchorTop;
    if (top === undefined) {
      const editor = editorRef.current;
      const caret = editor?.getCursorPosition();
      top = (caret && editor?.getPixelPositionForPosition(caret.lineNumber, 1)?.top) ?? 0;
    }
    setPicker({ open: true, anchorTop: top, source });
  }, [editorRef]);

  const setOpen = useCallback((next: boolean) => {
    setPicker((prev) => ({ ...prev, open: next }));
    if (!next) editorRef.current?.focus();
  }, [editorRef]);

  // Command palette → the active query tab only (one window event, one tab).
  useEffect(() => {
    if (!isActive || !enabled) return undefined;
    const onEvent = () => open('palette');
    window.addEventListener(QUERY_TABLE_EVENT, onEvent);
    return () => window.removeEventListener(QUERY_TABLE_EVENT, onEvent);
  }, [isActive, enabled, open]);

  // Close when the tab is deactivated (inactive tabs stay mounted).
  useEffect(() => {
    if (!isActive && picker.open) setPicker((prev) => ({ ...prev, open: false }));
  }, [isActive, picker.open]);

  const handlePick = useCallback(
    (target: TableSeedTarget, options: TableSeedPickOptions) => {
      const editor = editorRef.current;
      const sql = buildTableSeedSql(engineId, target.schema, target.name);
      if (!editor || !sql || !connectionId) return;

      const caret = editor.getCursorPosition() ?? { lineNumber: 1, column: 1 };
      const lineText = (editor.getValue() ?? '').split('\n')[caret.lineNumber - 1] ?? '';
      const lineEnd = lineText.length + 1;
      const blank = lineText.trim().length === 0;
      // Blank caret line → replace it with the statement. Non-blank (palette
      // fired mid-statement) → append on a fresh line below, never splicing
      // into the user's text.
      const edit = blank
        ? { range: { startLineNumber: caret.lineNumber, startColumn: 1, endLineNumber: caret.lineNumber, endColumn: lineEnd }, text: sql }
        : { range: { startLineNumber: caret.lineNumber, startColumn: lineEnd, endLineNumber: caret.lineNumber, endColumn: lineEnd }, text: `\n${sql}` };
      if (!editor.executeEdits?.('table-seed', [edit])) return;

      // Park the caret ON the statement (before its `;`) so the cursor-
      // statement tracking classifies it and the compact editor anchors to it.
      const line = blank ? caret.lineNumber : caret.lineNumber + 1;
      const column = Math.max(1, sql.length);
      editor.setSelectionRange({ startLineNumber: line, startColumn: column, endLineNumber: line, endColumn: column });
      editor.focus();

      useRecentSeedTablesStore.getState().recordSeed(connectionId, database, target.key);
      // engine / source / kind only — never the table name (no PII / high-cardinality props).
      track('feature:query_table_seed', {
        engine: engineId,
        source: picker.source,
        kind: target.kind,
        openEditor: options.openEditor,
      });

      setPicker((prev) => ({ ...prev, open: false }));
      executeStatement(sql);
      if (options.openEditor) openCompactEdit('table-seed');
    },
    [editorRef, engineId, connectionId, database, track, picker.source, executeStatement, openCompactEdit],
  );

  return {
    enabled,
    picker,
    groups,
    loading: !!connectionId && pickerObjects === undefined && tableListError === undefined,
    loadError: tableListError,
    retryLoad,
    open,
    setOpen,
    handlePick,
  };
}
